/**
 * DEMO · Idea 4 — Cambió el plano: ¿cuánto cambia mi presupuesto?
 *
 * Real (del modelo): la comparación A vs. B por GlobalId en el navegador (nuevo / quitado / modificado /
 * cambió de tipo) y las cantidades medidas de cada revisión con la medición de ci. Cada renglón de la
 * HP se recalcula con las dos revisiones. De ejemplo: la revisión B (se generó a propósito para el demo)
 * y los conceptos y precios unitarios.
 */
import { useMemo, useRef, useState } from 'react';
import { Button } from '@fluentui/react-components';
import { ChevronDown16Regular, ChevronRight16Regular } from '@fluentui/react-icons';
import { DemoVisor3D, type Pintura, type VisorDemoHandle } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel, estiloTabla } from '../../components/demos/DemoMarco.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import {
  HP_EJEMPLO,
  cant,
  cantidadDe,
  coloresDemo,
  conceptoDe,
  crearContexto,
  etiquetaElemento,
  pesos,
  r2cm,
  type ConceptoEjemplo,
} from '../../components/demos/logicaDemo.js';
import datos from '../../components/demos/datos/cambio-de-plano.json';
import { brandingProductoActivo } from '../../lib/brandingDelProducto.js';
import catalogo from '../../components/demos/datos/catalogo.json';

const FICHA = catalogo.demos.find((d) => d.id === 'cambio-de-plano')!;
const MAGENTA = brandingProductoActivo('ci').c1;
type TipoCambio = 'nuevo' | 'quitado' | 'modificado' | 'concepto';

interface CambioPieza {
  tipo: TipoCambio;
  a: ElementoDemo | null;
  b: ElementoDemo | null;
  conceptoA: ConceptoEjemplo | null;
  conceptoB: ConceptoEjemplo | null;
  detalle: string;
}

interface Renglon {
  c: ConceptoEjemplo;
  antes: number;
  ahora: number;
  importeAntes: number;
  importeAhora: number;
  cambios: CambioPieza[];
}

/** Firma de lo que se mide, dónde está y de qué tipo es, a la precisión con la que se reporta (cm). */
function firma(e: ElementoDemo): string {
  const c = e.caja;
  const pos = c ? [...c.min, ...c.max].map(r2cm).join(',') : '';
  return [e.area, e.longitud, e.volumen].map((v) => (v == null ? '' : r2cm(v))).join('|') + '@' + pos + '#' + e.tipo;
}

function comparar(A: ModeloDemo, B: ModeloDemo) {
  const ctxA = crearContexto(A.elementos);
  const ctxB = crearContexto(B.elementos);
  const porGidA = new Map(A.elementos.map((e) => [e.gid, e]));
  const porGidB = new Map(B.elementos.map((e) => [e.gid, e]));
  const piezas: CambioPieza[] = [];
  for (const b of B.elementos) {
    const a = porGidA.get(b.gid);
    const cb = conceptoDe(b, ctxB);
    if (!a) {
      piezas.push({ tipo: 'nuevo', a: null, b, conceptoA: null, conceptoB: cb, detalle: 'Aparece en la revisión B' });
      continue;
    }
    const ca = conceptoDe(a, ctxA);
    if (ca?.id !== cb?.id) {
      piezas.push({ tipo: 'concepto', a, b, conceptoA: ca, conceptoB: cb, detalle: `Cambió de tipo en el modelo: de «${a.tipo}» a «${b.tipo}»` });
    } else if (firma(a) !== firma(b)) {
      const partes: string[] = [];
      if (r2cm(a.area ?? 0) !== r2cm(b.area ?? 0)) partes.push('cambió su área');
      if (r2cm(a.volumen ?? 0) !== r2cm(b.volumen ?? 0)) partes.push('cambió su volumen');
      if (r2cm(a.longitud ?? 0) !== r2cm(b.longitud ?? 0)) partes.push('cambió su largo');
      if (a.tipo !== b.tipo) partes.push(`cambió de tipo («${a.tipo}» → «${b.tipo}»)`);
      piezas.push({ tipo: 'modificado', a, b, conceptoA: ca, conceptoB: cb, detalle: partes.length ? partes.join(', ') : 'se movió de lugar' });
    }
  }
  for (const a of A.elementos)
    if (!porGidB.has(a.gid)) piezas.push({ tipo: 'quitado', a, b: null, conceptoA: conceptoDe(a, ctxA), conceptoB: null, detalle: 'Ya no está en la revisión B' });

  // Cada renglón de la HP con su cantidad en A y en B. Las piezas que cambiaron de concepto van en su
  // propio renglón de la tabla, así que aquí no se cuentan (para que no aparezcan dos veces).
  const cambiaronDeConcepto = new Set(piezas.filter((p) => p.tipo === 'concepto').map((p) => p.b!.gid));
  const suma = (M: ModeloDemo, ctx: ReturnType<typeof crearContexto>, c: ConceptoEjemplo) =>
    M.elementos
      .filter((e) => !cambiaronDeConcepto.has(e.gid) && conceptoDe(e, ctx)?.id === c.id)
      .reduce((s, e) => s + cantidadDe(e, c.magnitud), 0);
  const renglones: Renglon[] = HP_EJEMPLO.conceptos
    .map((c) => {
      const antes = suma(A, ctxA, c);
      const ahora = suma(B, ctxB, c);
      return {
        c,
        antes,
        ahora,
        importeAntes: antes * c.pu,
        importeAhora: ahora * c.pu,
        cambios: piezas.filter((p) => p.tipo !== 'concepto' && (p.conceptoA?.id === c.id || p.conceptoB?.id === c.id)),
      };
    })
    .filter((r) => r.antes > 0 || r.ahora > 0);
  // Un renglón cambia si cambia su cantidad o si alguna de sus piezas cambió (aunque se compense).
  const cambia = (r: Renglon) => r2cm(r.antes) !== r2cm(r.ahora) || r.cambios.length > 0;
  const cambian = renglones.filter(cambia);
  const iguales = renglones.filter((r) => !cambia(r));
  return { piezas, cambian, iguales };
}

const TEXTO: Record<TipoCambio, { t: string; color: string }> = {
  nuevo: { t: 'Nuevo', color: 'var(--ok)' },
  quitado: { t: 'Quitado', color: 'var(--bad)' },
  modificado: { t: 'Modificado', color: 'var(--warn)' },
  concepto: { t: 'Cambió de concepto', color: MAGENTA },
};

export function DemoCambioDePlano() {
  const visor = useRef<VisorDemoHandle>(null);
  const [modelos, setModelos] = useState<Record<string, ModeloDemo> | null>(null);
  const [vista, setVista] = useState<'comparar' | 'a' | 'b'>('comparar');
  const [foco, setFoco] = useState<string | null>(null);
  const [verIguales, setVerIguales] = useState(false);
  const colores = useMemo(() => coloresDemo(), []);
  const r = useMemo(() => (modelos?.a && modelos?.b ? comparar(modelos.a, modelos.b) : null), [modelos]);
  const cambiosConcepto = r?.piezas.filter((p) => p.tipo === 'concepto') ?? [];
  /** Renglón propio de una pieza que cambió de concepto: su cantidad con el concepto de antes y el de ahora. */
  const deConcepto = cambiosConcepto.map((p) => {
    const qa = p.conceptoA ? cantidadDe(p.a!, p.conceptoA.magnitud) : 0;
    const qb = p.conceptoB ? cantidadDe(p.b!, p.conceptoB.magnitud) : 0;
    return { p, qa, qb, antes: p.conceptoA ? qa * p.conceptoA.pu : 0, ahora: p.conceptoB ? qb * p.conceptoB.pu : 0 };
  });
  const total =
    (r ? r.cambian.reduce((s, x) => s + x.importeAhora - x.importeAntes, 0) : 0) + deConcepto.reduce((s, x) => s + x.ahora - x.antes, 0);

  const tipoDeClave = useMemo(() => {
    const m = new Map<string, TipoCambio>();
    for (const p of r?.piezas ?? []) {
      if (p.tipo === 'quitado' && p.a) m.set(p.a.clave, 'quitado');
      if (p.b) m.set(p.b.clave, p.tipo);
    }
    return m;
  }, [r]);
  const clavesFoco = useMemo(() => {
    const ren = r?.cambian.find((x) => x.c.id === foco);
    return new Set((ren?.cambios ?? []).flatMap((p) => [p.a?.clave, p.b?.clave]).filter((x): x is string => !!x));
  }, [r, foco]);

  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null, clave: string): Pintura => {
        const deA = clave.startsWith('a:');
        if (vista === 'a') return deA ? {} : { visible: false };
        if (vista === 'b') return deA ? { visible: false } : {};
        if (!el) return { visible: false };
        const t = tipoDeClave.get(clave);
        const tenue = foco != null && !clavesFoco.has(clave);
        if (deA) return t === 'quitado' ? { color: colores.bad, opacidad: tenue ? 0.15 : 0.5 } : { visible: false };
        if (t === 'nuevo') return { color: colores.ok, opacidad: tenue ? 0.25 : 1 };
        if (t === 'modificado') return { color: colores.warn, opacidad: tenue ? 0.25 : 1 };
        if (t === 'concepto') return { color: colores.acento, opacidad: tenue ? 0.25 : 1 };
        return { color: colores.neutro, opacidad: 0.12 };
      },
    [vista, tipoDeClave, foco, clavesFoco, colores],
  );

  const flecha = (a: string, b: string) => (
    <span style={{ whiteSpace: 'nowrap' }}>
      {a} <span style={{ color: 'var(--text-tertiary)' }}>→</span> <b>{b}</b>
    </span>
  );

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(460px, 1.2fr)', gap: 14 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            <Button appearance={vista === 'comparar' ? 'primary' : 'secondary'} onClick={() => setVista('comparar')}>
              Comparar A contra B
            </Button>
            <Button appearance={vista === 'a' ? 'primary' : 'secondary'} onClick={() => setVista('a')}>
              {datos.revisionA.nombre}
            </Button>
            <Button appearance={vista === 'b' ? 'primary' : 'secondary'} onClick={() => setVista('b')}>
              {datos.revisionB.nombre}
            </Button>
          </div>
          <DemoVisor3D
            ref={visor}
            fuentes={[
              { clave: 'a', url: datos.revisionA.url },
              { clave: 'b', url: datos.revisionB.url },
            ]}
            onListo={setModelos}
            pintar={pintar}
            onPick={(clave) => {
              const p = r?.piezas.find((x) => x.b?.clave === clave || x.a?.clave === clave);
              const c = p?.conceptoB ?? p?.conceptoA;
              setFoco(c ? c.id : null);
            }}
            alto={540}
          />
          <div style={{ display: 'flex', gap: 16, fontSize: 'var(--fs-200)', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
            {(['nuevo', 'modificado', 'concepto', 'quitado'] as const).map((t) => (
              <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <span style={{ width: 12, height: 12, borderRadius: 3, background: TEXTO[t].color, opacity: t === 'quitado' ? 0.6 : 1 }} />
                {TEXTO[t].t}
                {t === 'quitado' ? ' (se ve como fantasma)' : ''}
              </span>
            ))}
          </div>
        </div>

        <Panel titulo="¿Qué cambió en el presupuesto?">
          <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>
            {datos.revisionA.nombre} ({datos.revisionA.fecha}) contra {datos.revisionB.nombre} ({datos.revisionB.fecha})
          </div>
          <div style={{ background: 'var(--bg-raised)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 'var(--fs-600)', fontWeight: 700 }}>{r ? `${total >= 0 ? '+' : '−'}${pesos(Math.abs(total))}` : '…'}</span>
            <span style={{ color: 'var(--text-secondary)' }}>
              {r ? `${r.cambian.length + deConcepto.length} renglones cambian · ${r.iguales.length} sin cambio` : 'Comparando…'}
            </span>
          </div>

          <table style={estiloTabla.tabla}>
            <thead>
              <tr>
                <th style={estiloTabla.th}>Concepto que cambió</th>
                <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Cantidad antes → ahora</th>
                <th style={{ ...estiloTabla.th, textAlign: 'right' }}>P. unitario</th>
                <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Importe antes → ahora</th>
                <th style={{ ...estiloTabla.th, textAlign: 'right' }}>Diferencia</th>
              </tr>
            </thead>
            <tbody>
              {deConcepto.map(({ p, qa, qb, antes, ahora }) => {
                const d = ahora - antes;
                const ca = p.conceptoA;
                const cb = p.conceptoB;
                return (
                  <tr
                    key={p.b!.clave}
                    onClick={() => {
                      setFoco(null);
                      setVista('comparar');
                      visor.current?.encuadrar([p.b!.clave]);
                    }}
                    style={{ cursor: 'pointer', boxShadow: `inset 3px 0 0 ${MAGENTA}` }}
                  >
                    <td style={estiloTabla.td}>
                      <div style={{ fontWeight: 600 }}>
                        {etiquetaElemento(p.b!)} · <span style={{ color: MAGENTA }}>cambió de tipo</span>
                      </div>
                      <div style={{ fontSize: 'var(--fs-200)' }}>
                        {ca ? `${ca.clave} ${ca.descripcion} ${pesos(ca.pu)}` : 'Sin concepto'} <span style={{ color: MAGENTA }}>→</span>{' '}
                        <b>{cb ? `${cb.clave} ${cb.descripcion} ${pesos(cb.pu)}` : 'Sin concepto'}</b>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{p.detalle}</div>
                    </td>
                    <td style={estiloTabla.num}>
                      {flecha(ca ? `${cant(qa, ca.magnitud)} ${ca.unidad}` : '—', cb ? `${cant(qb, cb.magnitud)} ${cb.unidad}` : '—')}
                    </td>
                    <td style={estiloTabla.num}>{flecha(ca ? pesos(ca.pu) : '—', cb ? pesos(cb.pu) : '—')}</td>
                    <td style={estiloTabla.num}>{flecha(pesos(antes), pesos(ahora))}</td>
                    <td style={{ ...estiloTabla.num, fontWeight: 700, color: d < 0 ? 'var(--ok)' : 'var(--text-primary)' }}>
                      {d >= 0 ? '+' : '−'}
                      {pesos(Math.abs(d))}
                    </td>
                  </tr>
                );
              })}
              {(r?.cambian ?? []).map((x) => {
                const d = x.importeAhora - x.importeAntes;
                const activo = foco === x.c.id;
                return (
                  <tr
                    key={x.c.id}
                    onClick={() => {
                      const nuevo = activo ? null : x.c.id;
                      setFoco(nuevo);
                      setVista('comparar');
                      const claves = x.cambios.flatMap((p) => [p.b?.clave, p.a?.clave]).filter((k): k is string => !!k);
                      visor.current?.encuadrar(nuevo && claves.length ? claves : undefined);
                    }}
                    style={{ cursor: 'pointer', background: activo ? 'var(--accent-weak)' : undefined }}
                  >
                    <td style={estiloTabla.td}>
                      <div style={{ fontWeight: 600 }}>
                        {x.c.clave} · {x.c.descripcion}
                      </div>
                      {x.cambios.map((p) => (
                        <div key={(p.b ?? p.a)!.clave} style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                          <span style={{ color: TEXTO[p.tipo].color }}>{TEXTO[p.tipo].t}</span> · {etiquetaElemento((p.b ?? p.a)!)}
                          {p.tipo === 'concepto' ? (p.conceptoB?.id === x.c.id ? ' (entra aquí)' : ' (sale de aquí)') : ` · ${p.detalle}`}
                        </div>
                      ))}
                    </td>
                    <td style={estiloTabla.num}>{flecha(cant(x.antes, x.c.magnitud), `${cant(x.ahora, x.c.magnitud)} ${x.c.unidad}`)}</td>
                    <td style={estiloTabla.num}>
                      {pesos(x.c.pu)}/{x.c.unidad}
                    </td>
                    <td style={estiloTabla.num}>{flecha(pesos(x.importeAntes), pesos(x.importeAhora))}</td>
                    <td style={{ ...estiloTabla.num, fontWeight: 700, color: d < 0 ? 'var(--ok)' : 'var(--text-primary)' }}>
                      {d >= 0 ? '+' : '−'}
                      {pesos(Math.abs(d))}
                    </td>
                  </tr>
                );
              })}
              {r && r.iguales.length > 0 && (
                <tr onClick={() => setVerIguales((v) => !v)} style={{ cursor: 'pointer' }}>
                  <td colSpan={5} style={{ ...estiloTabla.td, color: 'var(--text-secondary)' }}>
                    {verIguales ? <ChevronDown16Regular /> : <ChevronRight16Regular />} {r.iguales.length} renglones sin cambio
                  </td>
                </tr>
              )}
              {verIguales &&
                r?.iguales.map((x) => (
                  <tr key={x.c.id} style={{ color: 'var(--text-tertiary)' }}>
                    <td style={estiloTabla.td}>
                      {x.c.clave} · {x.c.descripcion}
                    </td>
                    <td style={estiloTabla.num}>
                      {cant(x.ahora, x.c.magnitud)} {x.c.unidad}
                    </td>
                    <td style={estiloTabla.num}>{pesos(x.c.pu)}</td>
                    <td style={estiloTabla.num}>{pesos(x.importeAhora)}</td>
                    <td style={estiloTabla.num}>—</td>
                  </tr>
                ))}
            </tbody>
            {r && r.cambian.length > 0 && (
              <tfoot>
                <tr>
                  <td style={{ ...estiloTabla.td, fontWeight: 700 }} colSpan={4}>
                    Total del cambio
                  </td>
                  <td style={{ ...estiloTabla.num, fontWeight: 700 }}>
                    {total >= 0 ? '+' : '−'}
                    {pesos(Math.abs(total))}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
          <NotaEjemplo>
            Cada pieza se reconoce por su identificador del modelo, así que se sabe con certeza qué es nuevo, qué se quitó, qué cambió de
            medida y qué cambió de tipo. Las cantidades se midieron en las dos revisiones; los conceptos y precios son de ejemplo.{' '}
            {datos.aviso}
          </NotaEjemplo>
        </Panel>
      </div>
    </DemoMarco>
  );
}
